import { AppNS } from 'app';
import { getPresignedUrl } from 'app/api/file-upload';
import { parseUploadedFilesData } from 'app/helpers/parseUploadedFilesData';

import AwsS3 from '@uppy/aws-s3';
import Compressor from '@uppy/compressor';
import Uppy, { Restrictions, UppyFile } from '@uppy/core';
import ThumbnailGenerator from '@uppy/thumbnail-generator';

export interface FileUploadConfig {
  restrictionsNote: string;
  filesDropNote?: string;
  restrictionsRules: Restrictions;
  presignedUrl: {
    modelName: string;
    propertyName: string;
  };
  isWithThumbnail?: boolean;
  isWithCompressor?: any;
  isWithStatusBar?: boolean;
  isWithTus?: boolean;
  onUploadStart: (data: Array<string>) => void;
  onUploadEnd: (data: Array<AppNS.UploadedFileData>) => void;
  onRestrictionsFailed: (message: string) => void;
  onThumbnailGenerated?: (previewUrl: string, fileInfo: any) => void;
  onUploadSuccess?: (fileInfo: any, progress: any) => void;
  onFileAdded?: (file: UppyFile) => void;
  onFileRemoved?: (file: UppyFile) => void;
  onFileError?: (file: UppyFile | undefined) => void;
  onFileProgress?: (fileId: string, uploaded: number, total: number) => void;
  onCancelAll?: () => void;
}

export function createUppy(
  config: FileUploadConfig,
  id: string,
  autoProceed: boolean
): Uppy {
  const uppy = new Uppy({
    restrictions: config.restrictionsRules,
    // turned off to set up initial state
    autoProceed,
    onBeforeFileAdded: (
      currentFile: UppyFile,
      files: { [key: string]: any }
    ) => {
      if (Object.keys(files).includes(currentFile.id)) {
        return false;
      }

      return {
        ...currentFile,
        name: currentFile.name.toLowerCase(),
      };
    },
    id,
  });

  uppy.use(AwsS3, {
    getUploadParameters() {
      return getPresignedUrl(
        config.presignedUrl.modelName,
        config.presignedUrl.propertyName
      );
    },
  });

  if (config.isWithCompressor) {
    uppy.use(Compressor, config.isWithCompressor);
  }

  uppy.on('file-added', (file: UppyFile) => {
    config.onFileAdded && config.onFileAdded(file);
  });

  uppy.on('complete', (result) => {
    config.onUploadEnd(parseUploadedFilesData(result.successful));
  });

  uppy.on('upload', (result) => {
    config.onUploadStart(result.fileIDs);
  });

  uppy.on('restriction-failed', (file, error) => {
    config.onRestrictionsFailed(error.message);
  });

  uppy.on('upload-progress', (file, progress) => {
    config.onFileProgress &&
      config.onFileProgress(
        file?.id || '',
        progress.bytesUploaded,
        progress.bytesTotal
      );
  });

  uppy.on('upload-error', (file, error, response) => {
    config.onFileError && config.onFileError(file);
  });

  uppy.on('file-removed', (file, reason) => {
    if (reason !== 'removed-by-user') {
      // we handle removing by user in the component
      config.onFileRemoved && config.onFileRemoved(file);
    }
  });

  if (config.isWithThumbnail) {
    uppy.use(ThumbnailGenerator, {
      thumbnailWidth: 200,
      waitForThumbnailsBeforeUpload: true,
    });

    uppy.on('thumbnail:generated', (file, preview) => {
      config.onThumbnailGenerated && config.onThumbnailGenerated(preview, file);
    });
  }

  uppy.on('upload-success', (file: any, response: any) => {
    config.onUploadSuccess && config.onUploadSuccess(file, response);
  });

  uppy.on('upload-retry', (fileID) => {});

  return uppy;
}
